// src/logic/semantic/semantic-matcher.ts
// Semantic backstop (Sandbox v5.4.1)
// - Add roadblock signal + synonym
// - Stronger disambiguation: anti-Why-Role on leadership/metrics/reverse-interview shapes
// - Anti-PM when metrics signal present
// - Broaden proud phrase coverage (special/specially)
// - Keep prior gates/boosts/overlap logic

import type { CueNote } from "../../types";
import { semanticConfig } from "../../config/semantic.config";
import { matchSuppression } from "../../config/suppression";

// ---------- runtime overrides ----------
function lsNum(key: string, d: number) {
  try { const v = localStorage.getItem(key); if (v==null) return d;
        const n = Number(v); return Number.isFinite(n)? n : d; } catch { return d; }
}
function lsBool(key: string, d: boolean) {
  try { const v = localStorage.getItem(key); if (v==null) return d;
        const s = v.toLowerCase(); return s==="true" ? true : s==="false" ? false : d; } catch { return d; }
}
const DBG = () => lsBool("cb_debug_semantic", false);
const OV_TAU = () => lsNum("cb_sem_tau", semanticConfig.tau);
const OV_TAU_TITLE = () => lsNum("cb_sem_tautitle", semanticConfig.tauTitle ?? Math.min(0.99, semanticConfig.tau + 0.05));
const OV_MIN_OVERLAP = () => lsNum("cb_sem_minOverlap", semanticConfig.minOverlap);

// ---------- tiny stopword down-weight ----------
const STOPWORDS = new Set([
  "the","a","an","and","or","but","if","so","to","for","of","on","in","at","by","with","from","about",
  "that","this","it","is","are","was","were","be","been","being","do","did","does","have","has","had",
  "you","your","they","their","them","we","our","i","me","my"
]);

// ---------- synonyms (Sandbox only) ----------
const SEMANTIC_SYNONYMS: Array<[RegExp, string]> = [
  // Weaknesses / growth
  [/\bblind\s*spot(s)?\b/gi, " weaknesses "],
  [/\bdo\s+better\b/gi, " weaknesses "],
  [/\bareas?\s+they('?|’)?re\s+working\s+on\b/gi, " weaknesses "],
  [/\bareas?\s+(you('|)re|you\s+are)\s+working\s+on\b/gi, " weaknesses "],
  [/\b(manager|boss)\s+(would|will|did)\s+(say|share|mention)\b/gi, " weaknesses "],
  [/\b(delivery|delibery|deliberetely)\b/gi, " deliberately "],
  [/\bdeliberately\b/gi, " deliberately "],
  [/\b(deliberately|trying\s+to\s+get\s+better|improv(ing|e)?)\b/gi, " weaknesses "],

  // Strengths / reputation / elevator pitch split
  [/\bcome\s+to\s+you\s+for\b/gi, " strengths "],
  [/\bpeople\s+rely\s+on\s+you\s+for\b/gi, " strengths "],
  [/\bwhat\s+are\s+you\s+known\s+for\b/gi, " strengths "],
  [/\bhow\s+would\s+(they|someone)\s+describe\s+you\b/gi, " elevator pitch strengths "],
  [/\bdescribe\s+you\b/gi, " strengths "],
  [/\breputation\b/gi, " strengths "],
  [/\bgo[-\s]?to\s+person\b/gi, " strengths "],
  [/\b(colleagues?|coworkers?)\b.*\bgo[-\s]?to\s+person\b/gi, " strengths "],

  // Elevator pitch
  [/\bhow\s+do\s+people\s+describe\s+you\b/gi, " elevator pitch "],
  [/\b(quick|thirty|30)[-\s]?(sec(ond)?|s)\s+(intro|summary)\b/gi, " elevator pitch "],
  [/\b(elevator|quick)\s+pitch\b/gi, " elevator pitch "],
  [/\bwhat\s+should\s+i\s+know\s+about\s+you\b/gi, " elevator pitch "],

  // Leadership / mentoring
  [/\b(mentor(ing)?|coached?|develop(ed|ing)?|grew)\s+(someone|others|a\s+teammate|a\s+colleague)\b/gi, " leadership "],
  [/\bpeople\s+development\b/gi, " leadership "],
  [/\bhelp(ed|ing)?\s+(someone|others)\s+grow\b/gi, " leadership "],

  // Conflict / Went Wrong
  [/\bblew\s+up\b/gi, " went wrong "],
  [/\bwent\s+south\b/gi, " went wrong "],
  [/\bfirefight(?:ing)?\b/gi, " went wrong "],
  [/\bescalat(?:e|ed|ion)\b/gi, " went wrong "],
  [/\b(setback|post[- ]?mortem|retrospective)\b/gi, " went wrong "],
  [/\bdidn'?t\s+go\s+as\s+planned\b/gi, " went wrong "],
  [/\bw(ent)?\s+sideways\b/gi, " went wrong "],
  [/\bhit\s+(a|the)\s+wall\b/gi, " went wrong "],
  [/\broadblock(s)?\b/gi, " went wrong roadblock "], // NEW: bridge "roadblock" directly

  // Influence / stakeholder mgmt / alignment
  [/\brally(ing)?\b/gi, " influence "],
  [/\b(get|got|getting)\s+people\s+on\s+board\b/gi, " influence "],
  [/\bbring(ing)?\s+people\s+along\b/gi, " influence "],
  [/\bbring(ing)?\s+(people\s+)?on\s?board\b/gi, " influence "],
  [/\bwin\s+hearts\s+and\s+minds\b/gi, " influence "],
  [/\bbuy-?\s?in\b/gi, " influence "],
  [/\b(align|alignment)(?:ing)?\b/gi, " influence "],
  [/\b(persuade|convince|sociali[sz]e)\b/gi, " influence "],
  [/\bnon[-\s]?report(ing)?\b/gi, " influence "],
  [/\bcross[-\s]?functional\b/gi, " influence "],
  [/\bguide\s+others\b/gi, " influence "],
  [/\bstakeholder(s)?\b/gi, " influence "],
  [/\bmultiple\s+stakeholders\b/gi, " influence "],
  [/\bstep\s+up\s+and\s+guide\s+others\b/gi, " influence "],
  [/\b(?:weren('?|’)t|not)\s+on\s+the\s+same\s+page\b/gi, " influence "],
  [/\btake\s+the\s+lead\s+(informally|without\s+authority)?\b/gi, " influence "],
  [/\bstep\s+in\s+and\s+take\s+the\s+lead\b/gi, " influence "],
  [/\b(get|got|getting)\s+(everyone|people|the\s+group|the\s+team)\s+aligned\b/gi, " influence "],
  [/\bon\s+the\s+same\s+page\b/gi, " influence "],
  [/\b(without|w\/o)\s+authority\b/gi, " influence "],
  [/\bgroup\s+aligned\b/gi, " influence "],
  [/\bmultiple\s+stakeholders?.*?(want|wanting|ask|asking|need|needing|request|requesting)\s+different\s+things\b/gi, " influence tradeoffs "],

  // Project Management / execution
  [/\bkeep\s+things\s+moving\b/gi, " project management "],
  [/\bmove\s+things\s+forward\b/gi, " project management "],
  [/\bkeep\s+projects?\s+on\s+track\b/gi, " project management "],
  [/\borganized\s+and\s+on\s+time\b/gi, " project management "],
  [/\b(what|how)\s+.*(gets|get)\s+priority\b/gi, " project management "],
  [/\beverything\s+feels\s+urgent\b/gi, " project management "],
  [/\b(competing\s+priorities|trade[-\s]?offs)\b/gi, " project management "],

  // Why this role/company
  [/\bwhat\s+made\s+this\s+(role|one)\s+stand\s+out\b/gi, " why this role "],
  [/\bstand\s+out\b/gi, " why this role "],
  [/\bwhy\s+(this\s+)?role\b/gi, " why this role "],
  [/\bthis\s+particular\s+role\b/gi, " why this role "],
  [/\bright\s+next\s+step\b/gi, " why this role "],
  [/\bwhat\s+was\s+it\s+about\s+this\s+company\b/gi, " why this company "],
  [/\bwhy\s+(this\s+)?company\b/gi, " why this company "],
  [/\b(drew\s+me|appealed|resonated|attracted)\b/gi, " why this role "],
  [/\bwhy\s+(you\s+)?applied\s+here\b/gi, " why this company "],
  [/\bpick\s+(one|1)\s+reason.*applied\s+here\b/gi, " why this company "],
  [/\bwhy\s+did\s+you\s+choose\s+(us|this\s+company)\b/gi, " why this company "],
  [/\bchoose\s+us\b/gi, " why this company "],
  [/\bwhat\s+would\s+it\s+be\??\b/gi, " why this company "],

  // Reverse interview
  [/\bwhat\s+would\s+you\s+like\s+to\s+ask\s+me\b/gi, " reverse interview "],
  [/\bquestions?\s+for\s+(me|us|the\s+team)\b/gi, " reverse interview "],
  [/\bbefore\s+we\s+(finish|wrap)\b.*\bask\b/gi, " reverse interview "],
  [/\banything\s+you('|)d\s+like\s+to\s+know(\s+from\s+me)?\b/gi, " reverse interview "],
  [/\bhow\s+(our|the)\s+team\s+(operates|works|runs)\b/gi, " reverse interview "],

  // Metrics / outcomes / success / evidence
  [/\bkeep\s+an\s+eye\s+on\b/gi, " metrics "],
  [/\b(numbers|outcomes|kpis?|metrics)\b/gi, " metrics "],
  [/\bhitting\s+the\s+mark\b/gi, " metrics "],
  [/\bwhat\s+does\s+success\s+look\s+like\b/gi, " metrics "],
  [/\b(measure|measuring)\s+success\b/gi, " metrics "],
  [/\bevidence\s+of\s+impact\b/gi, " metrics "],
  [/\b(prove|demonstrate|show)\s+(your\s+)?impact\b/gi, " metrics "],
  [/\bwhat\s+evidence\b.*\b(point|show|cite)\b/gi, " metrics "],

  // Journey → background
  [/\bjourney\b/gi, " background "],

  // Career goals
  [/\b(next\s+couple\s+of\s+years?|1[-\s]*3\s+years?|career\s+goal(s)?)\b/gi, " career goals "],
  [/\b(aim|aiming|working)\s+for\s+professionally\b/gi, " career goals "],
  [/\bprofessional\s+goals?\b/gi, " career goals "],

  // Achievement / proud (broaden)
  [/\b(proudest|especially\s+proud|special(?:ly|ty)?\s+proud)\b/gi, " achievement "], // NEW: special/specially/specialty
  [/\bbig(gest)?\s+win(s)?\b/gi, " achievement "],
  [/\baccomplishment(s)?|achievement(s)?\b/gi, " achievement "],

  // Feedback & growth / coachability
  [/\btough(est)?\s+piece\s+of\s+feedback\b/gi, " feedback "],
  [/\bhow\s+did\s+you\s+respond\s+to\s+feedback\b/gi, " feedback "],
  [/\bcoach(able|ability)\b/gi, " feedback "],
];

// ---------- title aliasing / augmentation ----------
const TITLE_AUGMENT: Record<string, string> = {
  "influence": " influence stakeholder buy-in align rally persuade convince socialize cross-functional non-reporting guide others multiple stakeholders tradeoffs disagreement same page without authority take the lead informally step in and take the lead get people aligned leadership bring people along hearts and minds bring onboard group aligned ",
  "project management": " project management execution delivery timeline on track organized on time manage stakeholders scheduling prioritization priority urgent trade-offs competing priorities handle situations group aligned same page multiple stakeholders want different things ",
  "reverse interview": " reverse interview questions for us questions for me ask before we wrap anything you'd like to know how the team operates ",
  "strengths": " strengths describe you reputation known for go-to person colleagues say go-to rely come to you for superpower proud proudest achievement accomplishments wins how would they describe you ",
  "weaknesses": " weaknesses growth areas improve working on development deliberately trying to get better feedback coachability toughest feedback respond to feedback ",
  "why this role": " why this role stand out appealed resonated attracted choose this role this particular role right next step ",
  "why this company": " why this company why us choose this company why you applied here why did you choose us pick 1 reason applied here pick one reason choose us what would it be choose us ",
  "metrics": " metrics kpi numbers outcomes hitting the mark measure success evidence impact success look like prove impact evidence would you point to ",
  "career goals": " career goals next years growth plan professional goals aiming professionally next couple of years ",
  "background": " background journey path ",
  "achievement": " achievement accomplishments proudest biggest win proud wins especially proud specially proud ",
  "achievements": " achievement accomplishments proudest biggest wins especially proud specially proud ",
  "feedback": " feedback coaching growth respond to feedback tough feedback coachability ",
  "elevator pitch": " elevator pitch quick intro describe you how they describe you 30 second intro who you are quick summary ",
  "star — project roadblock": " went wrong roadblock setback post-mortem retrospective conflict escalation ",
  "star- project roadblock": " went wrong roadblock setback post-mortem retrospective conflict escalation ",
  "star— project roadblock": " went wrong roadblock setback post-mortem retrospective conflict escalation ",

  "star — leadership": " leadership mentor mentoring coached developed grew people development helped others grow ",
  "star- leadership": " leadership mentor mentoring coached developed grew people development helped others grow ",
  "star— leadership": " leadership mentor mentoring coached developed grew people development helped others grow ",
};

function canonTitleRaw(title: string): string {
  return (title || "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/\s*—\s*|—|–|-/g, " - ")
    .replace(/\s+/g, " ")
    .trim();
}
function augmentTitle(title: string): string {
  const ct = canonTitleRaw(title);
  const aug = TITLE_AUGMENT[ct];
  return aug ? (ct + " " + aug) : ct;
}

// ---------- normalization & helpers ----------
const PUNCT_RE = /[^\p{L}\p{N}\s']/gu;
const MULTISPACE_RE = /\s+/g;

function applySemanticSynonyms(s: string): string {
  let t = s;
  for (const [re, rep] of SEMANTIC_SYNONYMS) t = t.replace(re, rep);
  return t;
}
function normalizeBase(s: string): string {
  let t = (s || "").toLowerCase().normalize("NFKC");
  t = t.replace(/\bwhy\s*s\b/g, "why us").replace(/\bwhy\s+ys\b/g, "why us");
  t = applySemanticSynonyms(t);
  t = t.replace(PUNCT_RE, " ");
  t = t.replace(MULTISPACE_RE, " ").trim();
  return t;
}
function toks(s: string): string[] { return normalizeBase(s).split(" ").filter(Boolean); }

function contentOverlap(a: string, b: string): number {
  const A = new Set(toks(a)), B = new Set(toks(b));
  if (!A.size || !B.size) return 0;
  let inter = 0; A.forEach(w => { if (B.has(w)) inter++; }); return inter;
}
function trigramVec(s: string): Map<string, number> {
  const m = new Map<string, number>(), str = normalizeBase(s).replace(/\s+/g, " ");
  const words = str.split(" ").filter(Boolean);
  const weighted = words.map(w => STOPWORDS.has(w) ? `${w.repeat(1)}` : `${w}${w}` ).join(" ");
  const src = weighted.length > 3 ? weighted : str;
  for (let i = 0; i <= src.length - 3; i++) {
    const g = src.slice(i, i+3);
    m.set(g, (m.get(g)||0)+1);
  }
  return m;
}
function cosine(a: Map<string, number>, b: Map<string, number>): number {
  let dot=0, na=0, nb=0; a.forEach(v=>na+=v*v); b.forEach(v=>nb+=v*v);
  const denom = Math.sqrt(na)*Math.sqrt(nb) || 1;
  const sm = a.size<=b.size ? a : b, lg = a.size<=b.size ? b : a;
  sm.forEach((v,g)=>{ const u = lg.get(g); if (u) dot+=v*u; });
  return dot/denom;
}

// ---------- starter-only restriction ----------
const STARTER_ID_RE = /^core\d+-/i;
function allowForPacket(notes: CueNote[]): boolean {
  if (!semanticConfig.starterOnly) return true;
  if (!notes?.length) return false;
  return notes.every(n => STARTER_ID_RE.test(n.id || ""));
}

// ---------- roadblock gating ----------
function isRoadblockNote(note: CueNote): boolean {
  const t = ((note.title||"") + " " + (note.id||"")).toLowerCase();
  return t.includes("roadblock") || t.includes("went wrong");
}
function hasRoadblockSignal(utteranceNorm: string): boolean {
  // NEW: include roadblock(s)
  return /\b(went wrong|blew up|went south|firefight(?:ing)?|issue|issues|problem|problems|mistake|mistakes|challenge|challenges|roadblock|roadblocks|blocker|blockers|escalat(?:e|ed|ion)|setback|sideways|didn'?t\s+go\s+as\s+planned|hit\s+(a|the)\s+wall)\b/.test(utteranceNorm);
}

// ---------- concept boost ----------
function hasWord(s: string, w: string) { return new RegExp(`\\b${w}\\b`, "i").test(s); }

const leadershipVerbSignalRe = /(rally|persuade|convince|buy[- ]?in|without\s+authority|take\s+the\s+lead|step\s+in\s+and\s+take\s+the\s+lead|guide\s+others|hearts\s+and\s+minds)/i;
const INFLUENCE_UTT_RE = /(influence|stakeholder|buy[- ]?in|rally|align|persuade|convince|sociali[sz]e|cross[-\s]?functional|non[-\s]?report|guide\s+others|multiple\s+stakeholders|tradeoffs?|disagreement|same\s+page|without\s+authority|take\s+the\s+lead|step\s+in\s+and\s+take\s+the\s+lead|bring(ing)?\s+people\s+along|bring(ing)?\s+(people\s+)?on\s?board|get.*aligned|group\s+aligned)/i;
const PM_UTT_RE = /(how\s+do\s+you\s+handle|handle\s+situations?|group\s+aligned|same\s+page|multiple\s+stakeholders.*(want|need|ask|request)\s+different\s+things|competing\s+priorities|trade[-\s]?offs?)/i;

const PM_TITLE_RE = /(project|program|execution|delivery|timeline|on track|organized|on time|project management)/i;
const WEAKNESS_UTT_RE = /(areas?\s+(you('|)re|you\s+are|they('?|’)re)\s+working\s+on|do\s+better|trying\s+to\s+get\s+better|improv(?:e|ing)|deliberately)/i;
const STAKEHOLDER_DISAGREE_RE = /(multiple\s+stakeholders?.*?(want|ask|need|request).*(different\s+things|different\s+outcomes)|stakeholder\s+(tradeoffs?|trade[-\s]?offs|disagreement|conflict)|same\s+page|get.*aligned)/i;

const GO_TO_SIGNAL_RE = /(go[-\s]?to\s+person|come\s+to\s+you\s+for|rely\s+on\s+you\s+for|colleagues?.*go[-\s]?to)/i;
const LEADERSHIP_MENTOR_RE = /(mentor(ing)?|coached?|develop(ed|ing)?|help(ed|ing)\s+.*grow|people\s+development)/i;

const EXACT_TITLE_WINS = new Set([
  "strengths","weaknesses","influence","project management",
  "why this role","why this company","metrics","reverse interview",
  "career goals","background","achievement","achievements","feedback",
  "elevator pitch",
  "star — project roadblock","star- project roadblock","star— project roadblock",
  "star — leadership","star- leadership","star— leadership"
]);

type BoostResult = { boost: number; reasons: string[] };

function conceptBoost(utter: string, canonTitle: string, roadblockNote: boolean, roadblockSignal: boolean): BoostResult {
  const reasons: string[] = [];
  let b = 0;

  // direct title tokens
  if (hasWord(utter, "strengths") && /strength/i.test(canonTitle)) { b += 0.06; reasons.push("strengths~title"); }
  if (hasWord(utter, "weaknesses") && /(weak|growth|improv|area\b.*work)/i.test(canonTitle)) { b += 0.06; reasons.push("weaknesses~title"); }

  // Weaknesses phrase nudge
  if (WEAKNESS_UTT_RE.test(utter)) { b += 0.02; reasons.push("weaknesses-phrase"); }

  // Leadership mentoring boost
  if (LEADERSHIP_MENTOR_RE.test(utter) && /\bleadership\b/i.test(canonTitle)) {
    b += 0.20; reasons.push("leadership-mentoring");
  }
  if (LEADERSHIP_MENTOR_RE.test(utter) && /strengths/i.test(canonTitle)) {
    b -= 0.06; reasons.push("anti-strengths-for-leadership");
  }

  // Influence vs PM arbitration
  const rawInfluence = INFLUENCE_UTT_RE.test(utter) || STAKEHOLDER_DISAGREE_RE.test(utter);
  const pmSignal = PM_UTT_RE.test(utter);
  const hasLeadershipVerbs = leadershipVerbSignalRe.test(utter);
  const influenceSignal = rawInfluence && (hasLeadershipVerbs || !pmSignal);

  if (pmSignal && PM_TITLE_RE.test(canonTitle)) { b += 0.12; reasons.push("pm-signal"); }
  if (influenceSignal && /(influence|leadership|stakeholder|buy[- ]?in|align|tradeoffs?|disagreement|same\s+page|without\s+authority)/i.test(canonTitle)) {
    b += 0.18; reasons.push("influence-cluster");
  }
  if (STAKEHOLDER_DISAGREE_RE.test(utter) && /(influence|leadership|stakeholder|tradeoffs?|conflict|alignment)/i.test(canonTitle)) {
    b += 0.05; reasons.push("stakeholder-disagreement");
  }
  if (influenceSignal && PM_TITLE_RE.test(canonTitle)) { b -= 0.10; reasons.push("anti-pm-bias"); }

  // PM fallback
  if (!influenceSignal && /(project management|keep.*moving|on track|timeline|organized|on time|gets?\s+priority|feels\s+urgent|competing\s+priorities|trade[-\s]?offs)/i.test(utter)
      && PM_TITLE_RE.test(canonTitle)) { b += 0.06; reasons.push("pm-cluster"); }

  // Went wrong / roadblock
  if (/(went wrong|blew up|issue|problem|mistake|challenge|escalat|setback|went sideways|hit (a|the) wall|didn'?t go as planned|roadblock|roadblocks)/i.test(utter)
      && /(went wrong|roadblock|conflict|post[- ]?mortem|setback|challenge)/i.test(canonTitle)) { b += 0.08; reasons.push("roadblock-cluster"); }

  // Why-Role / Why-Company
  if (/why this role|stand out|appealed|resonated|attracted|this\s+particular\s+role|right\s+next\s+step/i.test(utter)
      && /why this role/i.test(canonTitle)) { b += 0.22; reasons.push("why-role-strong-phrase"); }
  const whyCompanyBase =
    /why this company|why us|why (you )?applied here|pick (one|1) reason.*applied here|why did you choose (us|this company)|choose us/i.test(utter)
    && /why this company|why us/i.test(canonTitle);
  if (whyCompanyBase) { b += 0.24; reasons.push("why-company"); }
  if (/(pick\s+(one|1)\s+reason).*(applied here|choose\s+us).*(what\s+would\s+it\s+be\??)/i.test(utter)
      && /why this company|why us/i.test(canonTitle)) {
    b += 0.12; reasons.push("why-company-pick-reason-qform");
  }
  // NEW: Anti-Why-Role when the utterance actually signals other intents
  if (/(scale|scaled|scaling|led|leadership|influenc(ed|e)|rally|without\s+authority)/i.test(utter) && /why this role/i.test(canonTitle)) {
    b -= 0.15; reasons.push("anti-why-role-for-leadership-signal");
  }
  if (/(questions?\s+for\s+(me|us|team)|anything you'd like to know|how (our|the) team (operates|works|runs))/i.test(utter)
      && /(why this role|why this company)/i.test(canonTitle)) {
    b -= 0.20; reasons.push("anti-why-for-reverse-interview");
  }

  // Elevator Pitch
  if (/(elevator pitch|quick (intro|summary)|how (would|do) (they|people) describe you|30\s*sec(ond)?\s*intro|who you are)/i.test(utter)
      && /elevator pitch|quick intro|describe you/i.test(canonTitle)) { b += 0.10; reasons.push("elevator-pitch"); }

  // Metrics / success
  const METRICS_SIG = /(metrics|kpi|numbers|outcomes|hitting the mark|what does success look like|measure success|evidence of impact|prove (your )?impact|what evidence.*(point|show|cite))/i;
  if (METRICS_SIG.test(utter) && /(metric|kpi|outcome|success|evidence|impact)/i.test(canonTitle)) { b += 0.10; reasons.push("metrics"); }
  if (METRICS_SIG.test(utter) && /project management/i.test(canonTitle)) { b -= 0.10; reasons.push("anti-pm-when-metrics"); } // NEW: steer away from PM

  // Reverse interview
  if (/(reverse interview|ask me\b|questions for (me|us|team)|anything you'd like to know|how (our|the) team (operates|works|runs))/i.test(utter)
      && /reverse interview|questions for us/i.test(canonTitle)) { b += 0.08; reasons.push("reverse-interview"); }

  // Career goals
  if (/career goals|next .*years|aiming\s+for\s+professionally|professional\s+goals?/i.test(utter)
      && /career goals|growth plan|next .*years/i.test(canonTitle)) { b += 0.06; reasons.push("career-goals"); }

  // Strengths: go-to / colleagues signals
  if (GO_TO_SIGNAL_RE.test(utter) && /strength/i.test(canonTitle)) {
    b += 0.14; reasons.push("strengths-go-to");
  }

  // Achievement / proud → prefer Strengths
  const achSignal = /(proudest|especially proud|special(?:ly|ty)? proud|big(gest)? win|accomplishment|achievement|achievements)/i.test(utter);
  if (achSignal && /strengths/i.test(canonTitle)) { b += 0.26; reasons.push("strengths-from-proud"); }
  if (achSignal && /(achievement|achievements|accomplish|win|proud)/i.test(canonTitle)) { b += 0.08; reasons.push("achievement-generic"); }

  // Feedback / coachability → prefer Weaknesses
  const fbSignal = /(tough(est)? piece of feedback|respond(ed|ing)? to feedback|coach(able|ability))/i.test(utter);
  if (fbSignal && /weakness|growth|working on|improv/i.test(canonTitle)) { b += 0.22; reasons.push("weaknesses-from-feedback"); }
  if (fbSignal && /(feedback|coach|growth)/i.test(canonTitle)) { b += 0.10; reasons.push("feedback-generic"); }

  // Stronger if roadblock note AND signal present
  if (roadblockNote && roadblockSignal) { b += 0.06; reasons.push("roadblock-signal"); }

  // NEW: Anti-Why-Role when metrics present
  if (METRICS_SIG.test(utter) && /why this role/i.test(canonTitle)) {
    b -= 0.10; reasons.push("anti-why-role-for-metrics");
  }

  return { boost: b, reasons };
}

// ---------- semantic gates (helpers) ----------

function isInterrogativeBackground(utter: string): boolean {
  const hasBackground = /\bbackground\b/.test(utter);
  if (!hasBackground) return false;
  const askShape =
    /\b(tell\s+me|walk\s+me(\s+through)?|can\s+you|could\s+you|would\s+you|what('?|’)?s\s+your|what\s+is\s+your|give\s+me\s+(an\s+)?overview|how\s+do\s+you\s+introduce|how\s+would\s+you\s+introduce)\b/.test(utter)
    || /\?\s*$/.test(utter);
  return askShape;
}

function hasYouFutureProximity(utter: string): boolean {
  const tokens = utter.split(/\s+/);
  const idxYou = tokens.reduce<number[]>((a, w, i) => { if (/^(you|your)$/.test(w)) a.push(i); return a; }, []);
  const idxFut = tokens.reduce<number[]>((a, w, i) => {
    if (/^(future|plan|plans|next|12|12months|12-months|year|years|3|five|5)$/i.test(w)) a.push(i);
    return a;
  }, []);
  for (const i of idxYou) for (const j of idxFut) if (Math.abs(i - j) <= 6) return true;
  if (/\b(your|you).{0,24}(future|plan|next\s+(year|12|twelve)|3[-\s]*year|five[-\s]*year|5[-\s]*year)\b/i.test(utter)) return true;
  if (/\b(future|plan|next\s+(year|12|twelve)|3[-\s]*year|five[-\s]*year|5[-\s]*year).{0,24}(your|you)\b/i.test(utter)) return true;
  return false;
}

function isBackgroundTitle(ct: string) {
  return ct === "background" || ct === "elevator pitch";
}
function isCareerGoalsTitle(ct: string) {
  return ct === "career goals";
}

// ---------- public API ----------
export type SemanticHit = {
  id: string; title: string; trigger: string; score: number; stage: "semantic";
};

export function semanticBackstopMatch(utteranceRaw: string, notes: CueNote[]): SemanticHit | null {
  if (!semanticConfig.enabled) return null;
  if (!allowForPacket(notes)) return null;

  const tau = OV_TAU();
  const tauTitle = OV_TAU_TITLE();
  const minOverlap = OV_MIN_OVERLAP();

  const utteranceNorm = normalizeBase(utteranceRaw);
  if (!utteranceNorm) return null;

  // Deictic fragment abstain
  if (/^\s*(what|which)\s+are\s+they\??\s*$/.test(utteranceNorm)) {
    if (DBG()) console.info("[SEMANTIC] (abstain) deictic fragment:", utteranceNorm);
    return null;
  }

  // Semantic-only suppression pre-check
  const sup = matchSuppression(utteranceNorm);
  if (sup) {
    if (DBG()) console.info("[SEMANTIC] (suppression) abstain due to", sup.name, "utterance=", utteranceNorm);
    return null;
  }

  const uVec = trigramVec(utteranceNorm);
  let best: SemanticHit | null = null;

  const inspected: Array<{
    id: string; rawTitle: string; canonTitle: string; via: "title"|"trigger";
    trig?: string; score: number; overlap: number; skipped?: string; biases?: string[];
  }> = [];

  for (const note of notes) {
    const roadblockNote = isRoadblockNote(note);
    const roadblockSignal = hasRoadblockSignal(utteranceNorm);

    // ---- Title path ----
    const rawTitle = (note.title || "").trim();
    if (rawTitle) {
      const canonTitle = canonTitleRaw(rawTitle);
      const augmentedTitle = augmentTitle(rawTitle);
      const tVec = trigramVec(augmentedTitle);

      const { boost, reasons } = conceptBoost(utteranceNorm, canonTitle, roadblockNote, roadblockSignal);
      let score = cosine(uVec, tVec) + boost;

      if (EXACT_TITLE_WINS.has(canonTitle) && (hasWord(utteranceNorm, canonTitle) || hasWord(utteranceNorm, canonTitle.replace(/\s*—\s*|—|-/g," ")))) {
        score += 0.12;
        reasons.push("exact-title");
      }

      let skipped: string | undefined;
      if (roadblockNote && !roadblockSignal) skipped = "roadblock_no_signal_title";

      // Semantic gates (title path)
      if (!skipped && isBackgroundTitle(canonTitle) && !isInterrogativeBackground(utteranceNorm)) {
        skipped = "gate_background_interrogative";
      }
      if (!skipped && isCareerGoalsTitle(canonTitle) && !hasYouFutureProximity(utteranceNorm)) {
        skipped = "gate_career_you_future";
      }

      inspected.push({ id: note.id, rawTitle, canonTitle, via: "title", score, overlap: contentOverlap(utteranceNorm, canonTitle), skipped, biases: reasons });

      if (!skipped && score >= tauTitle) {
        const cand: SemanticHit = { id: note.id, title: note.title, trigger: `(title:${canonTitle})`, score, stage: "semantic" };
        if (!best || cand.score > best.score) best = cand;
      }
    }

    // ---- Trigger path ----
    const triggers = Array.isArray(note.triggers) ? note.triggers : [];
    const titleForBoost = canonTitleRaw(note.title || "");
    for (const trigRaw of triggers) {
      const trig = (trigRaw || "").trim();
      if (!trig) continue;

      const overlap = contentOverlap(utteranceNorm, trig);
      const tVec = trigramVec(trig);

      const { boost, reasons } = conceptBoost(utteranceNorm, titleForBoost, roadblockNote, roadblockSignal);
      let score = cosine(uVec, tVec) + boost;
      let skipped: string | undefined;

      if (overlap < minOverlap) skipped = "overlap_lt_min";
      if (!skipped && roadblockNote && !roadblockSignal) skipped = "roadblock_no_signal_trig";

      // Semantic gates (trigger path)
      if (!skipped && isBackgroundTitle(titleForBoost) && !isInterrogativeBackground(utteranceNorm)) {
        skipped = "gate_background_interrogative";
      }
      if (!skipped && isCareerGoalsTitle(titleForBoost) && !hasYouFutureProximity(utteranceNorm)) {
        skipped = "gate_career_you_future";
      }

      inspected.push({ id: note.id, rawTitle: note.title, canonTitle: titleForBoost, via: "trigger", trig: trigRaw, score, overlap, skipped, biases: reasons });

      if (!skipped && score >= tau) {
        const cand: SemanticHit = { id: note.id, title: note.title, trigger: trigRaw, score, stage: "semantic" };
        if (!best || cand.score > best.score) best = cand;
      }
    }
  }

  if (DBG()) {
    try {
      const top = inspected
        .sort((a,b)=>b.score-a.score)
        .slice(0,5)
        .map(x=>({
          id:x.id, via:x.via, trig:x.trig,
          rawTitle:x.rawTitle, canonTitle:x.canonTitle,
          score:+x.score.toFixed(3), overlap:x.overlap, skipped:x.skipped, biases:x.biases
        }));
      console.info("[SEMANTIC] (inspected top5)", {
        tau, tauTitle, minOverlap, utterance: utteranceNorm, top
      });
    } catch {}
  }

  if (process.env.NODE_ENV !== "production") {
    (window as any).__sem = (window as any).__sem || {};
    (window as any).__sem.semanticBackstopMatch = semanticBackstopMatch;
  }

  return best;
}
